'use strict';

var _ = require('lodash');

/**
 * Market Analysis - Market tab - Charts
 */
var MarketAnalysisTransformerFactory = function MarketAnalysisTransformerFactory($filter, DataTransformer, momentService, mergeScenarioDataService, metricReferenceDataService, dtdFilteringService) {
  var rmMomentFormat = $filter('rmMomentFormat');
  function MarketAnalysisTransformer() {
    DataTransformer.call(this, 'market', ['kpi-ribbon', 'date-range-level-chart', 'thematic-demand-chart-market-markets', 'thematic-demand-chart-any-dow', 'thematic-demand-chart-market-pos'], 0);
  }
  function getDaysInMonth(year, month) {
    var isLeap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  }

  // prepares a map of revenue day plan values for a given market for a given month
  function createTargetDataMap(data, marketDemands) {
    if (!marketDemands || marketDemands.length === 0) {
      return;
    }
    var dayMarketRecords = {};
    _.forEach(marketDemands, function (entry) {
      var date = entry.departureDate;
      if (_.has(dayMarketRecords, [date, entry.origin, entry.destination, entry.posName, 'quantity'])) {
        dayMarketRecords[date][entry.origin][entry.destination][entry.posName].quantity++;
      } else {
        _.set(dayMarketRecords, [date, entry.origin, entry.destination, entry.posName, 'quantity'], 1);
      }
    });
    _.forEach(data.target, function (d) {
      var daysInMonth = getDaysInMonth(d.year, d.month);
      var dayTarget = d.targetValue / daysInMonth;
      for (var i = 1; i <= daysInMonth; i++) {
        var date = momentService.of(d.year + '-' + d.month + '-' + i, momentService.FIELD.DEPARTURE_DATE, momentService.FORMAT.DATE_ISO);
        var dateString = rmMomentFormat(date, 'DATE_ISO');
        if (!_.has(dayMarketRecords, [dateString, d.marketOrigin, d.marketDestination, d.spos])) {
          continue;
        }
        var recordsQuantity = dayMarketRecords[dateString][d.marketOrigin][d.marketDestination][d.spos].quantity;
        dayMarketRecords[dateString][d.marketOrigin][d.marketDestination][d.spos][d.targetType] = dayTarget / recordsQuantity;
      }
    });
    return dayMarketRecords;
  }
  MarketAnalysisTransformer.prototype = Object.create(DataTransformer.prototype);
  MarketAnalysisTransformer.constructor = MarketAnalysisTransformer;
  MarketAnalysisTransformer.prototype.canTransform = function (sourceType, targetType) {
    return this.sourceType === sourceType && this.targetType.indexOf(targetType) > -1;
  };
  MarketAnalysisTransformer.prototype.isCacheable = function () {
    return true;
  };
  MarketAnalysisTransformer.prototype.getCacheKey = function (metrics, metricDescriptor) {
    var cacheKeys = [this.sourceType, metrics, metricDescriptor.dimension, _.get(metricDescriptor, 'params.selectedReference')];
    return _.chain(cacheKeys).flatten().join('-').value();
  };
  var getDimensionedData = function getDimensionedData(entry, date, memoizedDOWOfMoment, memoizedYearMonthDayOfMoment, dimension, selectedReference) {
    var entryKey = entry.carrier + entry.market + entry.posName + entry.departureDate;
    var flowTypePrefix = dimension || 'regular';
    var flowTypeKey = flowTypePrefix + 'Metrics';
    var flowTypeMetrics = entry[flowTypeKey];
    var newEntry = {
      entryKey: entryKey,
      origin: entry.origin,
      destination: entry.destination,
      market: entry.market,
      posName: entry.posName,
      departureDate: memoizedYearMonthDayOfMoment(date),
      departureDateMoment: date,
      departureDateMomentDDD: memoizedDOWOfMoment(date)
    };
    var metricsData = metricReferenceDataService.getMetricsData(['resHoldTotal', 'yield', 'revenue', 'revenuePaxDistance'], flowTypeMetrics, selectedReference);
    if (flowTypePrefix === 'regular') {
      newEntry.unadjustedDemand = entry.regularRemainingDemandFC;
      newEntry.adjustedDemand = entry.effectiveRegularSvcRemainingDemandFC;
    } else {
      newEntry.unadjustedDemand = entry.mappedRemainingDemandFC;
      newEntry.adjustedDemand = entry.effectiveMappedSvcRemainingDemandFC;
    }
    return _.merge(newEntry, metricsData);
  };
  MarketAnalysisTransformer.prototype.transform = function (data, metrics, metricDescriptor) {
    var dtdCriteria = _.get(metricDescriptor, 'params.dtdCriteria');
    var selectedReference = _.get(metricDescriptor, 'params.selectedReference');
    var dimension = metricDescriptor.dimension;
    var metricsKeys = [];
    var mlength = metrics.length;
    for (var m = 0; m < mlength; m++) {
      metricsKeys[m] = 'm' + (m + 1);
    }
    var memoizedMomentOfDepartureDate = _.memoize(getMomentOfDate);
    var memoizedDOWOfMoment = _.memoize(getDOWOfMoment, function (moment) {
      return moment.valueOf();
    });
    var memoizedYearMonthDayOfMoment = _.memoize(getYearMonthDayOfMoment, function (moment) {
      return moment.valueOf();
    });
    function transformData(marketDemand) {
      return _.transform(marketDemand, function (result, entry) {
        var date = memoizedMomentOfDepartureDate(entry.departureDate, momentService.FIELD.DEPARTURE_DATE);
        var newEntry = getDimensionedData(entry, date, memoizedDOWOfMoment, memoizedYearMonthDayOfMoment, dimension, selectedReference);
        for (m = 0; m < mlength; m++) {
          var curMetricName = metrics[m];
          newEntry[metricsKeys[m]] = newEntry[curMetricName];
        }
        return result.push(newEntry);
      });
    }
    var rawProductionData = data.domain.flattenedAggregatedMarketDemand;
    var rawScenarioData = data.thirdDomain && data.thirdDomain.flattenedAggregatedMarketDemand || [];
    var hasDtdCriteria = dtdCriteria && dtdCriteria.length;
    var dtdFilteredProductionData = hasDtdCriteria ? dtdFilteringService.filterByDtd(rawProductionData, dtdCriteria) : rawProductionData;
    var dtdFilteredScenarioData = hasDtdCriteria ? dtdFilteringService.filterByDtd(rawScenarioData, dtdCriteria) : rawProductionData;
    var productionData = transformData(dtdFilteredProductionData);
    var scenarioData = transformData(dtdFilteredScenarioData);
    var results = mergeScenarioDataService.merge(productionData, scenarioData);
    var targetDataMap = createTargetDataMap(data.secondDomain, results);
    for (var i = 0, length = results.length; i < length; i++) {
      var entry = results[i];
      entry.yieldPlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Yield;
      entry.bookingsPlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Bookings;
      entry.revenuePlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Revenue;
    }
    return results;
  };
  function getMomentOfDate(date, field) {
    return momentService.of(date, field);
  }
  function getDOWOfMoment(moment) {
    return rmMomentFormat(moment, 'DAY_OF_WEEK');
  }
  function getYearMonthDayOfMoment(moment) {
    return rmMomentFormat(moment, 'DATE_ISO');
  }
  return MarketAnalysisTransformer;
};
MarketAnalysisTransformerFactory.$inject = ['$filter', 'DataTransformerFactory', 'MomentService', 'MergeScenarioDataService', 'MetricReferenceDataService', 'DtdFilteringService'];
module.exports = MarketAnalysisTransformerFactory;